import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type {
  PermissionCatalogEntry,
  RoleWithPermissions,
} from "@forgepoint/shared";
import { useRolesStore } from "@/store/rolesStore";
import { PermissionCheckboxGroup } from "./PermissionCheckboxGroup";

interface RoleEditSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Called when the sheet is opened or closed */
  onOpenChange: (open: boolean) => void;
  /** The role being edited, or null to create a new role */
  role: RoleWithPermissions | null;
}

/**
 * Sheet for creating a custom role or editing an existing one: a name field
 * plus permission checkboxes grouped by area from the permission catalog.
 * @param props - RoleEditSheetProps
 * @returns Role editor sheet element
 */
export function RoleEditSheet({
  open,
  onOpenChange,
  role,
}: RoleEditSheetProps) {
  const permissionCatalog = useRolesStore((state) => state.permissionCatalog);
  const isLoadingCreate = useRolesStore((state) => state.isLoadingCreate);
  const loadingById = useRolesStore((state) => state.loadingById);
  const createRole = useRolesStore((state) => state.createRole);
  const updateRole = useRolesStore((state) => state.updateRole);

  const [name, setName] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const isSaving = role
    ? (loadingById[role.id]?.isLoadingSave ?? false)
    : isLoadingCreate;

  useEffect(() => {
    if (open) {
      setName(role?.name ?? "");
      setSelected(new Set(role?.permissions ?? []));
    }
  }, [open, role]);

  const groups = useMemo(
    () => groupCatalog(permissionCatalog),
    [permissionCatalog],
  );

  const handleToggle = (slug: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(slug);
      } else {
        next.delete(slug);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Role name is required");
      return;
    }

    const permissions = [...selected];
    const result = role
      ? await updateRole(role.id, { name: name.trim(), permissions })
      : await createRole(name.trim(), permissions);

    if (result) {
      toast.success(role ? "Role updated" : "Role created");
      onOpenChange(false);
      return;
    }
    toast.error(
      useRolesStore.getState().error ??
        useRolesStore.getState().loadingById[role?.id ?? ""]?.saveError ??
        "Failed to save role",
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[600px] md:max-w-[700px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{role ? "Edit Role" : "Create Role"}</SheetTitle>
          <SheetDescription>
            {role
              ? "Change the role's name and permissions"
              : "Name the role and choose the permissions it grants"}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          <div className="space-y-2">
            <Label htmlFor="role-name">Name</Label>
            <Input
              id="role-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter role name"
            />
          </div>

          <div className="space-y-5">
            <p className="text-sm font-medium">Permissions</p>
            {groups.map(({ group, entries }) => (
              <PermissionCheckboxGroup
                key={group}
                group={group}
                entries={entries}
                selected={selected}
                onToggle={handleToggle}
                disabled={isSaving}
              />
            ))}
          </div>
        </div>

        <SheetFooter className="flex-col sm:flex-row gap-2">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full sm:w-auto"
          >
            {role ? "Save" : "Create"}
            {isSaving && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
          </Button>
          <SheetClose asChild>
            <Button variant="outline" className="w-full sm:w-auto">
              Cancel
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Groups catalog entries by their group label, preserving catalog order.
 * @param catalog - The flat permission catalog
 * @returns Ordered groups, each with its entries
 */
function groupCatalog(
  catalog: PermissionCatalogEntry[],
): { group: string; entries: PermissionCatalogEntry[] }[] {
  const groups: { group: string; entries: PermissionCatalogEntry[] }[] = [];
  for (const entry of catalog) {
    const existing = groups.find((g) => g.group === entry.group);
    if (existing) {
      existing.entries.push(entry);
      continue;
    }
    groups.push({ group: entry.group, entries: [entry] });
  }
  return groups;
}
